import { RegistrationConsentState } from "@/components/auth/RegistrationConsentBlock";
import { LegalStatus } from "@/domains/shared/hooks/useLegalStatus";
import { IParentInvitePreview } from "@/domains/shared/models/IParentInvitePreview";
import { ISecretaryInvitePreview } from "@/domains/shared/models/ISecretaryInvitePreview";
import { IUser } from "@/domains/shared/models/IUser";
import { RegisterPayload } from "@/domains/shared/models/model";
import { api } from "@/domains/shared/services/api";
import { getBrowserTimeZoneId } from "@/domains/shared/utils/detectTimeZone";
import { buildRegistrationLocalePayload } from "@mivo/i18n";
import { AxiosRequestConfig } from "axios";

const authApiUrl = "/auth";

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
    _skipTokenRefresh?: boolean;
}

type BillingSummary = {
    subscriptionPlan: string;
    planName: string;
    patientLimit: number | null;
    activePatientCount: number;
    subscriptionStartedAt?: string | null;
    subscriptionEndsAt: string;
    accountAccessMode: string;
    paymentCommitmentOpen: boolean;
    billingHistoryAvailable: boolean;
};

export const authApi = {
    registerProfessional: async (payload: RegisterPayload) => {
        return await api.post<{ user: IUser }>(`${authApiUrl}/register`, {
            ...payload,
            ...buildRegistrationLocalePayload(),
        });
    },
    getParentInvitePreview: async (token: string) => {
        return await api.get<IParentInvitePreview>(`${authApiUrl}/parent-invite/${token}`);
    },
    registerParent: async (token: string, name: string, password: string, consent: RegistrationConsentState) => {
        return await api.post<{ user: IUser }>(`${authApiUrl}/register-parent`, {
            token,
            name,
            password,
            ...buildRegistrationLocalePayload(),
            acceptedTerms: consent.acceptedTerms,
            acceptedPrivacy: consent.acceptedPrivacy,
            acceptedRoleDeclaration: consent.acceptedRoleDeclaration,
        });
    },
    getSecretaryInvitePreview: async (token: string) => {
        return await api.get<ISecretaryInvitePreview>(`${authApiUrl}/secretary-invite/${token}`);
    },
    registerSecretary: async (token: string, name: string, password: string, consent: RegistrationConsentState) => {
        return await api.post<{ user: IUser }>(`${authApiUrl}/register-secretary`, {
            token,
            name,
            password,
            ...buildRegistrationLocalePayload(),
            acceptedTerms: consent.acceptedTerms,
            acceptedPrivacy: consent.acceptedPrivacy,
            acceptedRoleDeclaration: consent.acceptedRoleDeclaration,
        });
    },
    login: async (email: string, password: string) => {
        return await api.post<{ user: IUser }>(`${authApiUrl}/login`, { email, password, timeZoneId: getBrowserTimeZoneId() });
    },
    logout: async () => {
        return await api.post<void>(`${authApiUrl}/logout`);
    },
    me: async () => {
        return await api.get<{ user: IUser }>(`${authApiUrl}/me`);
    },
    patchMe: async (data: { name?: string; timeZoneId?: string; preferredLocale?: string; showEmail?: boolean; showPhone?: boolean; cpf?: string; councilNumber?: string; specialty?: string; postalCode?: string; street?: string; streetNumber?: string; neighborhood?: string; city?: string; state?: string; phone?: string }) => {
        return await api.patch<{ user: IUser }>(`${authApiUrl}/me`, {
            name: data.name,
            timeZoneId: data.timeZoneId,
            preferredLocale: data.preferredLocale,
            showEmail: data.showEmail,
            showPhone: data.showPhone,
            cpf: data.cpf,
            councilNumber: data.councilNumber,
            specialty: data.specialty,
            postalCode: data.postalCode,
            street: data.street,
            streetNumber: data.streetNumber,
            neighborhood: data.neighborhood,
            city: data.city,
            state: data.state,
            phone: data.phone
        });
    },
    realtimeToken: async () => {
        return await api.get(`${authApiUrl}/realtime-token`);
    },
    specialties: async () => {
        return await api.get<{ specialties: string[] }>(`${authApiUrl}/specialties`);
    },
    refresh: async () => {
        return await api.post(`${authApiUrl}/refresh`);
    },
    forgotPassword: async (email: string) => {
        return await api.post(`${authApiUrl}/forgot-password`, { email });
    },
    resetPassword: async (email: string, code: string, password: string) => {
        return await api.post(`${authApiUrl}/reset-password`, { email: email?.trim(), code: code?.trim(), newPassword: password?.trim() });
    },
    verifyEmail: async (code: string) => {
        return await api.post<{ user?: IUser; message?: string }>(`${authApiUrl}/verify-email`, { code });
    },
    verifyEmailRequest: async () => {
        return await api.post<void>(`${authApiUrl}/verify-email/request`);
    },

    getMyBilling: async () => {
        return await api.get<BillingSummary>(`${authApiUrl}/me/billing`);
    },
    getPlanAdjustmentNotice: async () => {
        return await api.get<{
            showBanner: boolean;
            code?: string | null;
            message?: string | null;
            adjustedAt?: string | null;
            params?: Record<string, string | number | boolean | null> | null;
        }>(`${authApiUrl}/me/plan-adjustment-notice`);
    },
    dismissPlanAdjustmentNotice: async () => {
        return await api.post<void>(`${authApiUrl}/me/plan-adjustment-notice/dismiss`);
    },
    getLegalStatus: async () => {
        return await api.get<LegalStatus>(`${authApiUrl}/legal-status`);
    },
    getExternalAuthProviders: async () => {
        return await api.get<{ google: boolean; apple: boolean }>(`${authApiUrl}/external/providers`);
    },
    requestTrialReactivation: async (reactivationToken: string) => {
        return await api.post<void>(`${authApiUrl}/trial-reactivation/request`, { reactivationToken });
    },
    confirmTrialReactivation: async (reactivationToken: string, code: string) => {
        return await api.post<{ user: IUser }>(`${authApiUrl}/trial-reactivation/confirm`, { reactivationToken, code, timeZoneId: getBrowserTimeZoneId() });
    },
    getAntiforgery: async () => {
        return await api.get<{ token?: string }>(`${authApiUrl}/antiforgery`, {
            _skipTokenRefresh: true,
        } as CustomAxiosRequestConfig);
    },
};